<template>
    <div class="ai-draft">
        <div class="ai-draft-page">

            <header class="ai-draft-header">
                <div class="ai-draft-header-inner">
                    <h1>
                        <i class="fa fa-magic"></i>
                        Draft with AI
                    </h1>
                    <p class="ai-draft-subtitle">
                        Describe a job and I'll draft a complete quote for you.
                    </p>
                </div>
            </header>

            <main class="ai-draft-main">

                <!-- Empty state: pick how to start -->
                <div v-if="!session" class="ai-draft-start">

                    <div class="ai-draft-card ai-draft-start-card">
                        <h2>Start a new draft</h2>
                        <p class="ai-draft-muted">Type a brief, paste an enquiry, or describe what the customer wants.</p>

                        <form class="ai-draft-start-form" @submit.prevent="startWithBrief">
                            <textarea
                                ref="briefInput"
                                v-model="briefText"
                                placeholder="e.g. Sarah at Tate, A2 limited-edition print, wants gallery-style framing under £600"
                                class="ai-draft-textarea"
                                rows="3"
                                @keydown.meta.enter.prevent="startWithBrief"
                                @keydown.ctrl.enter.prevent="startWithBrief"
                            ></textarea>

                            <div class="ai-draft-start-actions">
                                <button
                                    type="button"
                                    class="ai-draft-btn ai-draft-btn-ghost"
                                    @click="showEnquiryPaste = true"
                                >
                                    <i class="fa fa-envelope-open"></i>
                                    Paste an enquiry
                                </button>
                                <button
                                    type="submit"
                                    class="ai-draft-btn ai-draft-btn-primary"
                                    :disabled="!briefText.trim() || creating"
                                >
                                    <span v-if="creating"><i class="fa fa-spinner fa-spin"></i> Starting...</span>
                                    <span v-else>Start drafting <i class="fa fa-arrow-right"></i></span>
                                </button>
                            </div>
                        </form>
                    </div>

                    <in-progress-drafts
                        v-if="recentSessions.length > 0"
                        :sessions="recentSessions"
                        @resume="resumeSession"
                        @discarded="onSessionDiscarded"
                    ></in-progress-drafts>
                </div>

                <!-- Active session -->
                <chat-panel
                    v-else
                    :session="session"
                    :messages="messages"
                    :thinking="sending"
                    :error="error"
                    @send="sendUserMessage"
                    @action="handleSummaryAction"
                    @new-session="resetToEmpty"
                    @discard="discardCurrentSession"
                    @upload-image="uploadImage"
                ></chat-panel>

            </main>

            <enquiry-paste
                v-if="showEnquiryPaste"
                @cancel="showEnquiryPaste = false"
                @submit="startWithPastedEnquiry"
            ></enquiry-paste>

        </div>
    </div>
</template>

<script>
import api from '../../api';
import miniToastr from 'mini-toastr';
import ChatPanel from './ChatPanel.vue';
import InProgressDrafts from './InProgressDrafts.vue';
import EnquiryPaste from './EnquiryPaste.vue';

// mini-toastr requires init() before .error()/.success() work. Match the
// existing convention used by ProductionDashboard / ProductionProgress: init
// once on first mount of a top-level page component, scoped to the #messages
// element if present.
let miniToastrInited = false;
function ensureToastrInited() {
    if (miniToastrInited) return;
    try {
        miniToastr.init({
            timeout: 3000,
            appendTarget: document.getElementById('messages') || document.body,
        });
        miniToastrInited = true;
    } catch (e) { /* swallow — never let a notifier crash the page */ }
}

export default {
    components: { ChatPanel, InProgressDrafts, EnquiryPaste },

    props: {
        authUser: { type: Object, required: true },
    },

    data: () => ({
        session: null,
        messages: [],
        recentSessions: [],
        briefText: '',
        creating: false,
        sending: false,
        error: null,
        showEnquiryPaste: false,
    }),

    mounted() {
        ensureToastrInited();
        this.loadRecentSessions();
        this.$nextTick(() => {
            if (this.$refs.briefInput) this.$refs.briefInput.focus();
        });
    },

    methods: {
        // Celebrate the moment a draft becomes a real saved quote. Called
        // from the live update handlers (not a watcher) so resuming an
        // existing quote-bearing session doesn't re-fire it.
        celebrateIfQuoteJustLanded(hadQuoteBefore) {
            if (!hadQuoteBefore && this.session && this.session.quote_id) {
                this.fireConfetti();
            }
        },

        fireConfetti() {
            if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
            if (document.getElementById('ai-draft-confetti')) return;

            const canvas = document.createElement('canvas');
            canvas.id = 'ai-draft-confetti';
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
            document.body.appendChild(canvas);
            const ctx = canvas.getContext('2d');

            const colours = ['#e74c3c', '#f1c40f', '#2ecc71', '#3498db', '#9b59b6', '#e67e22'];
            const particles = [];
            for (let i = 0; i < 140; i++) {
                particles.push({
                    x: canvas.width / 2 + (Math.random() - 0.5) * canvas.width * 0.3,
                    y: canvas.height * 0.35,
                    vx: (Math.random() - 0.5) * 14,
                    vy: -(Math.random() * 12 + 4),
                    w: Math.random() * 8 + 4,
                    h: Math.random() * 5 + 3,
                    colour: colours[i % colours.length],
                    rot: Math.random() * Math.PI,
                    vr: (Math.random() - 0.5) * 0.3,
                });
            }

            const started = Date.now();
            const tick = () => {
                const elapsed = Date.now() - started;
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                particles.forEach(p => {
                    p.x += p.vx;
                    p.y += p.vy;
                    p.vy += 0.35;      // gravity
                    p.vx *= 0.99;      // drag
                    p.rot += p.vr;
                    ctx.save();
                    ctx.translate(p.x, p.y);
                    ctx.rotate(p.rot);
                    ctx.globalAlpha = Math.max(0, 1 - elapsed / 2500);
                    ctx.fillStyle = p.colour;
                    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                    ctx.restore();
                });
                if (elapsed < 2500) {
                    requestAnimationFrame(tick);
                } else {
                    canvas.remove();
                }
            };
            requestAnimationFrame(tick);
        },

        loadRecentSessions() {
            api.aiListSessions().then(res => {
                if (!res || !res.sessions) return;
                // Show only active sessions for the resume list — completed/abandoned
                // sessions live on the (future) history tab.
                this.recentSessions = res.sessions.filter(s => s.status === 'active');
            });
        },

        startWithBrief() {
            const text = this.briefText.trim();
            if (!text || this.creating) return;
            this.creating = true;
            this.error = null;

            api.aiCreateSession()
                .then(res => {
                    if (!res || !res.session) throw new Error('No session returned');
                    this.session = res.session;
                    this.messages = res.messages || [];
                    this.briefText = '';
                    return this.sendUserMessage(text);
                })
                .catch(e => {
                    this.error = (e && e.message) || 'Could not start a session.';
                })
                .then(() => {
                    this.creating = false;
                });
        },

        startWithPastedEnquiry(enquiryText) {
            this.showEnquiryPaste = false;
            const wrapped = "Here's an enquiry that just came in. Draft a quote.\n\n---\n\n" + enquiryText;
            this.briefText = wrapped;
            this.startWithBrief();
        },

        sendUserMessage(content) {
            if (!this.session || !content || this.sending) return Promise.resolve();
            this.sending = true;
            this.error = null;

            // Append the user message immediately. The backend persists it
            // (and emits a user_message event) but we don't replace this row
            // — the local copy stands in until full reload via aiGetSession.
            const localUserMessage = {
                id: 'local-' + Date.now(),
                seq: (this.messages[this.messages.length - 1] || {seq: 0}).seq + 1,
                role: 'user',
                content: content,
                tool_name: null,
                tool_params: null,
                tool_result: null,
                tool_use_id: null,
                created_at: new Date().toISOString(),
            };
            this.messages.push(localUserMessage);

            return api.aiStreamMessage(this.session.id, content, (type, data) => {
                this.handleStreamEvent(type, data);
            }).then(outcome => {
                if (outcome.sessionUpdate) {
                    const hadQuote = !!(this.session && this.session.quote_id);
                    this.session = Object.assign({}, this.session, outcome.sessionUpdate);
                    this.celebrateIfQuoteJustLanded(hadQuote);
                }
                if (outcome.error) this.error = outcome.error;
            }).catch(e => {
                // Hard network/HTTP failure: roll back the local user row so
                // the user knows the message wasn't received.
                this.messages = this.messages.filter(m => m.id !== localUserMessage.id);
                this.error = (e && e.message) || 'Something went wrong.';
            }).then(() => {
                this.sending = false;
            });
        },

        handleStreamEvent(type, data) {
            // assistant_start: open a new assistant text message in the chat
            if (type === 'assistant_start') {
                this.messages.push({
                    id: data.id,
                    seq: data.seq,
                    role: 'assistant',
                    content: '',
                    tool_name: null,
                    tool_params: null,
                    tool_result: null,
                    tool_use_id: null,
                    created_at: new Date().toISOString(),
                    _streaming: true,
                });
                return;
            }

            // text_delta: append to the assistant message Vue currently shows
            if (type === 'text_delta') {
                for (let i = this.messages.length - 1; i >= 0; i--) {
                    if (this.messages[i].id === data.id) {
                        // Preserve reactivity: replace via Vue.set since the
                        // string is reactive but we're concatenating in place.
                        this.$set(this.messages[i], 'content',
                            (this.messages[i].content || '') + (data.text || ''));
                        return;
                    }
                }
                return;
            }

            if (type === 'assistant_stop') {
                for (let i = this.messages.length - 1; i >= 0; i--) {
                    if (this.messages[i].id === data.id) {
                        this.$set(this.messages[i], '_streaming', false);
                        // Trust the server-emitted final content over our
                        // accumulated deltas in case of any drift.
                        if (typeof data.content === 'string') {
                            this.$set(this.messages[i], 'content', data.content);
                        }
                        return;
                    }
                }
                return;
            }

            if (type === 'tool_call') {
                this.messages.push({
                    id: data.id,
                    seq: data.seq,
                    role: 'tool_call',
                    content: null,
                    tool_name: data.tool_name,
                    tool_params: data.tool_params,
                    tool_result: null,
                    tool_use_id: data.tool_use_id,
                    created_at: new Date().toISOString(),
                });
                return;
            }

            if (type === 'tool_result') {
                this.messages.push({
                    id: data.id,
                    seq: data.seq,
                    role: 'tool_result',
                    content: null,
                    tool_name: null,
                    tool_params: null,
                    tool_result: data.tool_result,
                    tool_use_id: data.tool_use_id,
                    created_at: new Date().toISOString(),
                });
                return;
            }

            if (type === 'session_update' && data.session) {
                const hadQuote = !!(this.session && this.session.quote_id);
                this.session = Object.assign({}, this.session, data.session);
                this.celebrateIfQuoteJustLanded(hadQuote);
                return;
            }

            // user_message / complete / error are handled in the .then()
            // around aiStreamMessage; nothing to render incrementally.
        },

        uploadImage(file, done) {
            if (!this.session) { done(); return; }
            api.aiUploadImage(this.session.id, file).then(res => {
                if (res && res.message) {
                    this.messages.push(res.message);
                } else {
                    this.error = (res && res.error) || 'Image upload failed.';
                }
            }).catch(e => {
                this.error = (e && e.message) || 'Image upload failed.';
            }).then(done);
        },

        onSessionDiscarded(session) {
            // Drop locally instead of round-tripping the list endpoint —
            // server already marked it abandoned.
            this.recentSessions = this.recentSessions.filter(s => s.id !== session.id);
        },

        discardCurrentSession() {
            if (!this.session) return;
            const label = this.session.quote_id ? `Quote #${this.session.quote_id}` : 'this untitled draft';
            if (! confirm(`Discard ${label}? You can still see the conversation on the quote's history tab.`)) return;
            api.aiAbandonSession(this.session.id).then(() => {
                this.resetToEmpty();
            });
        },

        resumeSession(sessionStub) {
            api.aiGetSession(sessionStub.id).then(res => {
                if (!res || !res.session) return;
                this.session = res.session;
                this.messages = res.messages || [];
            });
        },

        resetToEmpty() {
            this.session = null;
            this.messages = [];
            this.error = null;
            this.loadRecentSessions();
            this.$nextTick(() => {
                if (this.$refs.briefInput) this.$refs.briefInput.focus();
            });
        },

        handleSummaryAction(payload) {
            // payload: {action: 'download_pdf'|'open_in_editor'|'cheaper'|'tweak', quoteId?}
            const action = payload.action;
            const quoteId = payload.quoteId || (this.session && this.session.quote_id);

            if (action === 'download_pdf' && quoteId) {
                window.open(`/quote/${quoteId}/pdf`, '_blank');
                this.completeSession();
                return;
            }
            if (action === 'open_in_editor' && quoteId) {
                this.completeSession().then(() => {
                    window.location.href = `/quote/${quoteId}`;
                });
                return;
            }
            if (action === 'cheaper') {
                this.sendUserMessage('Lower the price.');
                return;
            }
            if (action === 'tweak') {
                // ChatPanel focuses the input itself when it sees this.
                return;
            }
        },

        completeSession() {
            if (!this.session) return Promise.resolve();
            return api.aiCompleteSession(this.session.id).then(res => {
                if (res && res.session) this.session = res.session;
            });
        },
    },
};
</script>

<style>
/* Page-scoped CSS. All rules are namespaced under .ai-draft so legacy app
   styles aren't affected. */

.ai-draft {
    --ai-bg: #f7f8fa;
    --ai-card: #ffffff;
    --ai-text: #1f2937;
    --ai-muted: #6b7280;
    --ai-border: #e5e7eb;
    --ai-primary: #2563eb;
    --ai-primary-hover: #1d4ed8;
    --ai-user-tint: #eff6ff;
    --ai-tool-tint: #f3f4f6;
    --ai-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 4px 12px rgba(15, 23, 42, .06);
    --ai-radius: 12px;
    --ai-radius-sm: 8px;

    font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: var(--ai-text);
    background: var(--ai-bg);
    min-height: calc(100vh - 60px);
    padding-bottom: 40px;
}

.ai-draft *, .ai-draft *::before, .ai-draft *::after {
    box-sizing: border-box;
}

.ai-draft-page {
    max-width: 860px;
    margin: 0 auto;
    padding: 32px 16px 0;
}

.ai-draft-header {
    margin-bottom: 24px;
}

.ai-draft-header h1 {
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 6px;
    color: var(--ai-text);
}

.ai-draft-header h1 .fa {
    color: var(--ai-primary);
    font-size: 24px;
    margin-right: 6px;
}

.ai-draft-subtitle {
    color: var(--ai-muted);
    margin: 0;
    font-size: 15px;
}

.ai-draft-main {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Empty / start state */

.ai-draft-start {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.ai-draft-card {
    background: var(--ai-card);
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius);
    box-shadow: var(--ai-shadow);
    padding: 24px;
}

.ai-draft-start-card h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 4px;
}

.ai-draft-muted {
    color: var(--ai-muted);
    margin: 0 0 16px;
    font-size: 14px;
}

.ai-draft-textarea {
    display: block;
    width: 100%;
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius-sm);
    padding: 12px 14px;
    font: inherit;
    font-size: 15px;
    line-height: 1.5;
    color: var(--ai-text);
    background: #fff;
    resize: vertical;
    transition: border-color .15s, box-shadow .15s;
}

.ai-draft-textarea:focus {
    outline: none;
    border-color: var(--ai-primary);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .15);
}

.ai-draft-start-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
    flex-wrap: wrap;
}

.ai-draft-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid transparent;
    border-radius: var(--ai-radius-sm);
    padding: 9px 16px;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color .12s, border-color .12s, color .12s, transform .05s;
    line-height: 1.2;
}

.ai-draft-btn:disabled {
    opacity: .55;
    cursor: not-allowed;
}

.ai-draft-btn:not(:disabled):active {
    transform: translateY(1px);
}

.ai-draft-btn-primary {
    background: var(--ai-primary);
    color: #fff;
    border-color: var(--ai-primary);
}

.ai-draft-btn-primary:hover:not(:disabled) {
    background: var(--ai-primary-hover);
    border-color: var(--ai-primary-hover);
}

.ai-draft-btn-ghost {
    background: transparent;
    color: var(--ai-text);
    border-color: var(--ai-border);
}

.ai-draft-btn-ghost:hover:not(:disabled) {
    background: var(--ai-tool-tint);
}

.ai-draft-btn-secondary {
    background: #fff;
    color: var(--ai-primary);
    border-color: var(--ai-primary);
}

.ai-draft-btn-secondary:hover:not(:disabled) {
    background: var(--ai-user-tint);
}
</style>
